import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Leaf, Sparkles, Droplet, HeartHandshake, ChevronDown, ShieldCheck } from "lucide-react";
import Breadcrumbs from "@/components/Breadcrumbs";
import { useLanguage } from "@/i18n/LanguageContext";
import { FadeUp, Stagger, StaggerItem } from "@/components/Reveal";
import Seo from "@/components/Seo";

/* -------------------------------------------------------------------------- */
/* DATA — Trilingual (FR / EN / PT), aligned with NANDA SÁ premium identity */
/* -------------------------------------------------------------------------- */

const CATEGORIES = [
 {
 key: "body",
 label: { fr: "Soins Corps", en: "Body Care", pt: "Cuidados Corpo" },
 desc: {
 fr: "Laits, beurres, huiles et gommages pour nourrir la peau au quotidien.",
 en: "Milks, butters, oils and scrubs to nourish the skin every day.",
 pt: "Leites, manteigas, óleos e esfoliantes para nutrir a pele todos os dias.",
 },
 },
 {
 key: "face",
 label: { fr: "Soins Visage", en: "Face Care", pt: "Cuidados Rosto" },
 desc: {
 fr: "Sérums, huiles précieuses et rituels de peau, formulés courts et honnêtes.",
 en: "Serums, precious oils and skin rituals, formulated short and honestly.",
 pt: "Séruns, óleos preciosos e rituais de pele, formulados de forma curta e honesta.",
 },
 },
 {
 key: "hair",
 label: { fr: "Soins Cheveux", en: "Hair Care", pt: "Cuidados Cabelo" },
 desc: {
 fr: "Sérums capillaires, huiles et masques pour la brillance et la force.",
 en: "Hair serums, oils and masks for shine and strength.",
 pt: "Séruns capilares, óleos e máscaras para brilho e força.",
 },
 },
 {
 key: "oils",
 label: { fr: "Huiles Naturelles", en: "Natural Oils", pt: "Óleos Naturais" },
 desc: {
 fr: "Baobab, moringa, hibiscus, touloucouna. Pressions à froid, single-origin.",
 en: "Baobab, moringa, hibiscus, touloucouna. Cold-pressed, single-origin.",
 pt: "Baobá, moringa, hibisco, toulucuna. Prensadas a frio, single-origin.",
 },
 },
 {
 key: "shea",
 label: { fr: "Collection Karité", en: "Shea Butter Collection", pt: "Colecção Karité" },
 desc: {
 fr: "Beurres, chantillys et savons issus du karité de Guinée-Bissau.",
 en: "Butters, whipped textures and soaps from Guinea-Bissau shea.",
 pt: "Manteigas, chantillys e sabonetes de karité da Guiné-Bissau.",
 },
 },
 {
 key: "aloe",
 label: { fr: "Collection Aloe Vera", en: "Aloe Vera Collection", pt: "Colecção Aloé Vera" },
 desc: {
 fr: "Gels-crèmes hydratants, effet frais immédiat, formules apaisantes.",
 en: "Hydrating gel-creams, immediate fresh feel, soothing formulas.",
 pt: "Gel-cremes hidratantes, efeito fresco imediato, fórmulas calmantes.",
 },
 },
 {
 key: "botanical",
 label: { fr: "Ingrédients Botaniques", en: "Botanical Ingredients", pt: "Ingredientes Botânicos" },
 desc: {
 fr: "Matières premières nobles sourcées et transformées localement.",
 en: "Noble raw materials sourced and processed locally.",
 pt: "Matérias-primas nobres sourcadas e transformadas localmente.",
 },
 },
];

const HERO_PRODUCTS = [
 {
 slug: "chantilly-karite-coco",
 image: "/photos/nanda-cosmetiques/chantilly-karite-coco.png",
 name: { fr: "Chantilly Karité Coco", en: "Shea & Coconut Whipped Butter", pt: "Chantilly de Karité Coco" },
 category: { fr: "Soin Corps · Signature", en: "Body Care · Signature", pt: "Cuidado Corpo · Signature" },
 volume: "100 ml",
 benefits: {
 fr: "Nourrissant · Fondant · Réconfortant",
 en: "Nourishing · Melting · Comforting",
 pt: "Nutritivo · Fundente · Reconfortante",
 },
 origin: "Guinée-Bissau",
 },
 {
 slug: "karite-brut",
 image: "/photos/nanda-cosmetiques/ing-shea-hero.jpg",
 name: { fr: "Beurre de Karité brut", en: "Raw Shea Butter", pt: "Manteiga de Karité em bruto" },
 category: { fr: "Soin Multi-usage", en: "Multi-purpose Care", pt: "Cuidado Multiuso" },
 volume: "150 ml",
 benefits: {
 fr: "Réparateur · Filmogène · Nourrissant",
 en: "Repairing · Film-forming · Nourishing",
 pt: "Reparador · Filmogéneo · Nutritivo",
 },
 origin: "Guinée-Bissau",
 },
 {
 slug: "gel-aloe-vera",
 image: "/photos/nanda-cosmetiques/ing-aloe-hero.jpg",
 name: { fr: "Gel d'Aloe Vera", en: "Aloe Vera Gel", pt: "Gel de Aloé Vera" },
 category: { fr: "Hydratation · Fraîcheur", en: "Hydration · Freshness", pt: "Hidratação · Frescura" },
 volume: "100 ml",
 benefits: {
 fr: "Hydrate · Apaise · Rafraîchit",
 en: "Hydrates · Soothes · Refreshes",
 pt: "Hidrata · Alivia · Refresca",
 },
 origin: "Guinée-Bissau",
 },
 {
 slug: "huile-hibiscus",
 image: "/photos/nanda-cosmetiques/hibiscus-oil.png",
 name: { fr: "Huile d'Hibiscus", en: "Hibiscus Oil", pt: "Óleo de Hibisco" },
 category: { fr: "Huile Précieuse", en: "Precious Oil", pt: "Óleo Precioso" },
 volume: "50 ml",
 benefits: {
 fr: "Nourrit · Illumine · Fortifie",
 en: "Nourishes · Illuminates · Strengthens",
 pt: "Nutre · Ilumina · Fortalece",
 },
 origin: "Guinée-Bissau",
 },
 {
 slug: "huile-baobab",
 image: "/photos/nanda-cosmetiques/baobab-oil.png",
 name: { fr: "Huile de Baobab", en: "Baobab Oil", pt: "Óleo de Baobá" },
 category: { fr: "Visage · Corps · Cheveux", en: "Face · Body · Hair", pt: "Rosto · Corpo · Cabelo" },
 volume: "30 ml",
 benefits: {
 fr: "Régénère · Assouplit · Protège",
 en: "Regenerates · Softens · Protects",
 pt: "Regenera · Suaviza · Protege",
 },
 origin: "Guinée-Bissau",
 },
 {
 slug: "huile-moringa",
 image: "/photos/nanda-cosmetiques/moringa-oil.png",
 name: { fr: "Huile de Moringa", en: "Moringa Oil", pt: "Óleo de Moringa" },
 category: { fr: "Visage · Corps · Cheveux", en: "Face · Body · Hair", pt: "Rosto · Corpo · Cabelo" },
 volume: "30 ml",
 benefits: {
 fr: "Vitalité · Fraîcheur · Éclat",
 en: "Vitality · Freshness · Radiance",
 pt: "Vitalidade · Frescura · Brilho",
 },
 origin: "Guinée-Bissau",
 },
 {
 slug: "lait-corporel-karite",
 image: "/photos/nanda-cosmetiques/ing-moringa-hero.jpg",
 name: { fr: "Lait Corporel Karité", en: "Shea Body Milk", pt: "Leite Corporal de Karité" },
 category: { fr: "Hydratation Quotidienne", en: "Daily Hydration", pt: "Hidratação Diária" },
 volume: "200 ml",
 benefits: {
 fr: "Fluide · Léger · Enveloppant",
 en: "Fluid · Light · Enveloping",
 pt: "Fluido · Leve · Envolvente",
 },
 origin: "Guinée-Bissau",
 },
 {
 slug: "savon-artisanal-karite",
 image: "/photos/nanda-cosmetiques/ing-palm-real.png",
 name: { fr: "Savon Artisanal Karité", en: "Artisanal Shea Soap", pt: "Sabonete Artesanal de Karité" },
 category: { fr: "Nettoyant Doux", en: "Gentle Cleanser", pt: "Limpeza Suave" },
 volume: "100 g",
 benefits: {
 fr: "Doux · Naturel · Enrichi karité",
 en: "Gentle · Natural · Shea-enriched",
 pt: "Suave · Natural · Enriquecido com karité",
 },
 origin: "Guinée-Bissau",
 },
 {
 slug: "savon-noir-karite",
 image: "/photos/nanda-cosmetiques/savon-noir.jpg",
 name: { fr: "Savon Noir de Guinée-Bissau", en: "Bissau-Guinean Black Soap", pt: "Sabonete Preto da Guiné-Bissau" },
 category: { fr: "Nettoyant Traditionnel", en: "Traditional Cleanser", pt: "Limpeza Tradicional" },
 volume: "150 g",
 benefits: {
 fr: "Purifiant · Détoxifiant · Ancestral",
 en: "Purifying · Detoxifying · Ancestral",
 pt: "Purificante · Desintoxicante · Ancestral",
 },
 origin: "Guinée-Bissau",
 },
 {
 slug: "gommage-baobab",
 image: "/photos/nanda-cosmetiques/ing-baobab-hero.jpg",
 name: { fr: "Gommage Baobab & Miel", en: "Baobab & Honey Scrub", pt: "Esfoliante Baobá & Mel" },
 category: { fr: "Exfoliation Sensorielle", en: "Sensory Exfoliation", pt: "Esfoliação Sensorial" },
 volume: "150 ml",
 benefits: {
 fr: "Lisse · Éclaire · Régénère",
 en: "Smooths · Brightens · Regenerates",
 pt: "Suaviza · Ilumina · Regenera",
 },
 origin: "Guinée-Bissau",
 },
];

const VALUES = [
 { icon: Leaf, key: "natural", fr: "Naturel", en: "Natural", pt: "Natural" },
 { icon: Sparkles, key: "premium", fr: "Premium", en: "Premium", pt: "Premium" },
 { icon: HeartHandshake, key: "african", fr: "Africain", en: "African", pt: "Africano" },
 { icon: ShieldCheck, key: "authentic", fr: "Authentique", en: "Authentic", pt: "Autêntico" },
 { icon: Droplet, key: "responsible", fr: "Responsable", en: "Responsible", pt: "Responsável" },
];

const INGREDIENTS = [
 {
 name: "Baobab",
 latin: "Adansonia digitata",
 fr: "Anti-âge, régénérant, riche en oméga 3, 6 et 9.",
 en: "Anti-ageing, regenerating, rich in omega 3, 6 and 9.",
 pt: "Anti-envelhecimento, regenerador, rico em ómega 3, 6 e 9.",
 },
 {
 name: "Karité",
 latin: "Vitellaria paradoxa",
 fr: "Nourrissant profond, réparateur, cicatrisant.",
 en: "Deeply nourishing, repairing, healing.",
 pt: "Profundamente nutritivo, reparador, cicatrizante.",
 },
 {
 name: "Moringa",
 latin: "Moringa oleifera",
 fr: "Purifiant, vitaminé, antioxydant naturel.",
 en: "Purifying, vitamin-rich, natural antioxidant.",
 pt: "Purificante, rico em vitaminas, antioxidante natural.",
 },
 {
 name: "Hibiscus",
 latin: "Hibiscus sabdariffa",
 fr: "Illuminateur, tenseur naturel, AHA végétaux.",
 en: "Illuminating, natural firming, plant AHAs.",
 pt: "Iluminador, tensor natural, AHA vegetais.",
 },
 {
 name: "Aloe Vera",
 latin: "Aloe barbadensis",
 fr: "Hydratant intense, apaisant, réparateur.",
 en: "Intense hydration, soothing, repairing.",
 pt: "Hidratação intensa, calmante, reparador.",
 },
 {
 name: "Touloucouna",
 latin: "Carapa procera",
 fr: "Ingrédient rare ouest-africain, protecteur.",
 en: "Rare Bissau-Guinean ingredient, protective.",
 pt: "Ingrediente raro oeste-africano, protector.",
 },
];

const RITUAL = [
 {
 step: "01",
 fr: "Sourcing local",
 en: "Local sourcing",
 pt: "Sourcing local",
 descFr: "Ingrédients récoltés en Guinée-Bissau auprès de coopératives partenaires.",
 descEn: "Ingredients harvested in Guinea-Bissau from partner cooperatives.",
 descPt: "Ingredientes colhidos na Guiné-Bissau junto de cooperativas parceiras.",
 },
 {
 step: "02",
 fr: "Transformation",
 en: "Processing",
 pt: "Transformação",
 descFr: "Extraction à froid, filtrations douces, respect des savoir-faire traditionnels.",
 descEn: "Cold extraction, gentle filtration, respect for traditional know-how.",
 descPt: "Extracção a frio, filtrações suaves, respeito pelos saberes tradicionais.",
 },
 {
 step: "03",
 fr: "Formulation",
 en: "Formulation",
 pt: "Formulação",
 descFr: "Formules courtes, sans compromis, texture agréable, efficacité prouvée.",
 descEn: "Short, uncompromising formulas, pleasant texture, proven efficacy.",
 descPt: "Fórmulas curtas, sem compromisso, textura agradável, eficácia comprovada.",
 },
 {
 step: "04",
 fr: "Conditionnement",
 en: "Packaging",
 pt: "Embalagem",
 descFr: "Verre ambré, capsules durables, étiquettes éditoriales trilingues.",
 descEn: "Amber glass, durable caps, editorial trilingual labels.",
 descPt: "Vidro âmbar, cápsulas duradouras, etiquetas editoriais trilingues.",
 },
];

const FAQ = [
 {
 q: { fr: "Vos cosmétiques sont-ils 100 % naturels ?", en: "Are your cosmetics 100% natural?", pt: "Os cosméticos são 100% naturais?" },
 a: {
 fr: "Oui. Nos formules privilégient des ingrédients botaniques de Guinée-Bissau et excluent parabènes, silicones et parfums de synthèse. Chaque référence est formulée courte et honnête.",
 en: "Yes. Our formulas prioritise Guinea-Bissau botanical ingredients and exclude parabens, silicones and synthetic fragrances. Every reference is formulated short and honestly.",
 pt: "Sim. As nossas fórmulas privilegiam ingredientes botânicos da Guiné-Bissau e excluem parabenos, silicones e fragrâncias sintéticas. Cada referência é formulada de forma curta e honesta.",
 },
 },
 {
 q: { fr: "Où sont fabriqués les produits NANDA SÁ ?", en: "Where are NANDA SÁ products made?", pt: "Onde são fabricados os produtos NANDA SÁ?" },
 a: {
 fr: "Nos cosmétiques sont fabriqués en Guinée-Bissau, avec des matières premières issues de nos filières partenaires. La transformation locale est un pilier de notre engagement.",
 en: "Our cosmetics are made in Guinea-Bissau, using raw materials from our partner value chains. Local transformation is a pillar of our commitment.",
 pt: "Os nossos cosméticos são fabricados na Guiné-Bissau, com matérias-primas das nossas fileiras parceiras. A transformação local é um pilar do nosso compromisso.",
 },
 },
 {
 q: { fr: "Quels ingrédients privilégiez-vous ?", en: "Which ingredients do you prioritise?", pt: "Que ingredientes privilegia?" },
 a: {
 fr: "Nos formules mettent en valeur les trésors botaniques de la Guinée-Bissau : karité, baobab, moringa, hibiscus, touloucouna, aloe vera, miel forestier, huile de palme rouge artisanale.",
 en: "Our formulas showcase Guinea-Bissau's botanical treasures: shea, baobab, moringa, hibiscus, touloucouna, aloe vera, forest honey, artisanal red palm oil.",
 pt: "As nossas fórmulas valorizam os tesouros botânicos da Guiné-Bissau: karité, baobá, moringa, hibisco, toulucuna, aloé vera, mel florestal, óleo de palma vermelho artesanal.",
 },
 },
 {
 q: { fr: "Proposez-vous une distribution B2B ?", en: "Do you offer B2B distribution?", pt: "Oferecem distribuição B2B?" },
 a: {
 fr: "Oui. Hôtels, spas, revendeurs et distributeurs peuvent nous contacter via le formulaire Devis. Nous proposons des conditions dédiées et du co-branding sur demande.",
 en: "Yes. Hotels, spas, resellers and distributors can contact us via the Quote form. We offer dedicated conditions and co-branding on request.",
 pt: "Sim. Hotéis, spas, revendedores e distribuidores podem contactar-nos via o formulário de Orçamento. Oferecemos condições dedicadas e co-branding a pedido.",
 },
 },
 {
 q: { fr: "Les emballages sont-ils durables ?", en: "Is your packaging sustainable?", pt: "As embalagens são sustentáveis?" },
 a: {
 fr: "Nous privilégions le verre ambré recyclable, les capsules réutilisables et minimisons les composants plastiques. Nos étiquettes utilisent des encres à faible impact.",
 en: "We favour recyclable amber glass, reusable caps and minimise plastic components. Our labels use low-impact inks.",
 pt: "Privilegiamos o vidro âmbar reciclável, as cápsulas reutilizáveis e minimizamos os componentes plásticos. As nossas etiquetas utilizam tintas de baixo impacto.",
 },
 },
 {
 q: { fr: "Puis-je acheter en ligne ?", en: "Can I buy online?", pt: "Posso comprar online?" },
 a: {
 fr: "Notre boutique en ligne officielle sera lancée prochainement. En attendant, les commandes B2B, hôtels et revendeurs se font via le formulaire de contact.",
 en: "Our official online store will launch soon. In the meantime, B2B, hotel and reseller orders are placed via the contact form.",
 pt: "A nossa loja oficial online será lançada em breve. Entretanto, encomendas B2B, hotéis e revendedores fazem-se via o formulário de contacto.",
 },
 },
];

/* -------------------------------------------------------------------------- */
/* Helpers */
/* -------------------------------------------------------------------------- */

const pick = (obj, locale) => obj[locale] || obj.fr;

const Section = ({ children, className = "", id }) => (
 <section id={id} className={`border-t border-nanda-line ${className}`}>
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-20 md:py-28">{children}</div>
 </section>
);

const Overline = ({ children }) => (
 <div className="label-overline text-nanda-muted mb-4">{children}</div>
);

/* -------------------------------------------------------------------------- */
/* Page */
/* -------------------------------------------------------------------------- */

export default function NandaCosmetiques() {
 const { locale } = useLanguage();
 const [openFaq, setOpenFaq] = React.useState(null);

 const t = {
 heroH1: {
 fr: "Cosmétiques Naturels",
 en: "Natural Cosmetics",
 pt: "Cosméticos Naturais",
 }[locale] || "Cosmétiques Naturels",
 heroBy: { fr: "par NANDA SÁ", en: "by NANDA SÁ", pt: "por NANDA SÁ" }[locale],
 heroSub: {
 fr: "Nés des trésors botaniques de la Guinée-Bissau — karité, baobab, moringa, hibiscus, aloe vera — les cosmétiques naturels NANDA SÁ sont formulés à partir d'ingrédients soigneusement sélectionnés, pour un rituel de soin quotidien.",
 en: "Born from Guinea-Bissau's botanical treasures — shea, baobab, moringa, hibiscus, aloe vera — NANDA SÁ natural cosmetics are formulated from carefully selected ingredients, crafted for everyday wellbeing.",
 pt: "Nascidos dos tesouros botânicos da Guiné-Bissau — karité, baobá, moringa, hibisco, aloé vera — os cosméticos naturais NANDA SÁ são formulados a partir de ingredientes cuidadosamente seleccionados, para um ritual de cuidado diário.",
 }[locale],
 ctaDiscover: { fr: "Découvrir la collection", en: "Discover the collection", pt: "Descobrir a colecção" }[locale],
 ctaQuote: { fr: "Demander un devis B2B", en: "Request a B2B quote", pt: "Pedir um orçamento B2B" }[locale],
 categoriesTitle: { fr: "Catégories", en: "Categories", pt: "Categorias" }[locale],
 categoriesSubtitle: {
 fr: "Une gamme complète de soins, du corps au visage en passant par les cheveux et les collections signature.",
 en: "A complete range of care, from body to face, hair and signature collections.",
 pt: "Uma gama completa de cuidados, do corpo ao rosto passando pelos cabelos e colecções signature.",
 }[locale],
 productsTitle: { fr: "Produits Signature", en: "Signature Products", pt: "Produtos Signature" }[locale],
 productsSubtitle: {
 fr: "Quatre références iconiques qui incarnent l'essence de la Division Cosmétiques NANDA SÁ.",
 en: "Four iconic references that embody the essence of the NANDA SÁ Cosmetics Division.",
 pt: "Quatro referências icónicas que encarnam a essência da Divisão Cosméticos NANDA SÁ.",
 }[locale],
 valuesTitle: { fr: "Nos Valeurs", en: "Our Values", pt: "Os Nossos Valores" }[locale],
 ingredientsTitle: { fr: "Ingrédients Botaniques", en: "Botanical Ingredients", pt: "Ingredientes Botânicos" }[locale],
 ingredientsSubtitle: {
 fr: "Chaque plante a une histoire. Nous les sélectionnons pour leurs propriétés reconnues et leur ancrage ouest-africain.",
 en: "Every plant has a story. We select them for their proven properties and West African roots.",
 pt: "Cada planta tem uma história. Seleccionamo-las pelas suas propriedades reconhecidas e enraizamento oeste-africano.",
 }[locale],
 ritualTitle: { fr: "Notre Rituel", en: "Our Ritual", pt: "O Nosso Ritual" }[locale],
 ritualSubtitle: {
 fr: "De la récolte au flacon, un savoir-faire local et responsable.",
 en: "From harvest to bottle, a local and responsible craftsmanship.",
 pt: "Da colheita ao frasco, um saber-fazer local e responsável.",
 }[locale],
 faqTitle: { fr: "Questions fréquentes", en: "Frequently asked questions", pt: "Perguntas frequentes" }[locale],
 ctaFinal: {
 title: {
 fr: "Une gamme née d'Afrique de l'Ouest, pensée pour le monde.",
 en: "A range born from West Africa, crafted for the world.",
 pt: "Uma gama nascida da África Ocidental, pensada para o mundo.",
 }[locale],
 sub: {
 fr: "Distributeurs, hôtels, spas et revendeurs, contactez-nous pour découvrir nos conditions dédiées et le co-branding.",
 en: "Distributors, hotels, spas and resellers, contact us to discover our dedicated conditions and co-branding options.",
 pt: "Distribuidores, hotéis, spas e revendedores, contactem-nos para descobrir as nossas condições dedicadas e opções de co-branding.",
 }[locale],
 button: { fr: "Nous écrire", en: "Contact us", pt: "Contacte-nos" }[locale],
 },
 breadcrumb: { fr: "Cosmétiques Naturels", en: "Natural Cosmetics", pt: "Cosméticos Naturais" }[locale],
 };

 // JSON-LD Product ItemList — for Google rich snippets
 const productSchema = {
 "@context": "https://schema.org",
 "@type": "ItemList",
 name: pick({ fr: "Cosmétiques Naturels NANDA SÁ", en: "NANDA SÁ Natural Cosmetics", pt: "Cosméticos Naturais NANDA SÁ" }, locale),
 itemListElement: HERO_PRODUCTS.map((p, i) => ({
 "@type": "Product",
 position: i + 1,
 name: pick(p.name, locale),
 brand: { "@type": "Brand", name: "NANDA SÁ" },
 category: pick(p.category, locale),
 image: `https://nanda-sa.com${p.image}`,
 description: pick(p.benefits, locale),
 manufacturer: { "@type": "Organization", name: "NANDA SÁ", address: { "@type": "PostalAddress", addressCountry: "GW", addressLocality: "Bissau" } },
 })),
 };

 // JSON-LD FAQPage
 const faqSchema = {
 "@context": "https://schema.org",
 "@type": "FAQPage",
 mainEntity: FAQ.map((f) => ({
 "@type": "Question",
 name: pick(f.q, locale),
 acceptedAnswer: { "@type": "Answer", text: pick(f.a, locale) },
 })),
 };

 return (
 <div data-testid="page-cosmetiques" className="bg-white">
 <Seo path="/cosmetiques" />

 <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(productSchema) }} />
 <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

 {/* ================= HERO ================= */}
 <section className="relative overflow-hidden bg-[#F7F3EC]">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 pt-10 pb-24 md:pt-14 md:pb-32">
 <Breadcrumbs items={[{ label: t.breadcrumb, path: "/cosmetiques" }]} />

 <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mt-6 items-center">
 <FadeUp className="lg:col-span-7">
 <Overline>{{ fr: "Division Cosmétiques", en: "Cosmetics Division", pt: "Divisão Cosméticos" }[locale]}</Overline>
 <h1
 className="font-serif text-[13vw] leading-[0.92] tracking-[-0.02em] text-nanda-ink md:text-[7vw] lg:text-[6.4vw]"
 data-testid="cosmetiques-hero-title"
 >
 {t.heroH1}
 <br />
 <span className="italic font-light text-[#4A5D4E]">{t.heroBy}</span>
 </h1>
 <p className="mt-8 text-lg md:text-xl leading-relaxed text-nanda-muted max-w-2xl">
 {t.heroSub}
 </p>
 <div className="mt-10 flex flex-wrap gap-4">
 <a
 href="#collection"
 className="inline-flex items-center gap-2 bg-nanda-ink text-white px-8 py-4 text-sm tracking-wider uppercase hover:bg-[#4A5D4E] transition-colors"
 data-testid="cosmetiques-cta-collection"
 >
 {t.ctaDiscover}
 <ArrowUpRight className="w-4 h-4" />
 </a>
 <Link
 to="/devis"
 className="inline-flex items-center gap-2 border border-nanda-ink text-nanda-ink px-8 py-4 text-sm tracking-wider uppercase hover:bg-nanda-ink hover:text-white transition-colors"
 data-testid="cosmetiques-cta-quote"
 >
 {t.ctaQuote}
 </Link>
 </div>
 </FadeUp>

 <FadeUp className="lg:col-span-5" delay={0.2}>
 <div className="relative aspect-[3/4] bg-white rounded-sm overflow-hidden shadow-[0_30px_80px_-40px_rgba(0,0,0,0.3)]">
 <img
 src="/photos/nanda-cosmetiques/hibiscus-oil.png"
 alt={pick({ fr: "Huile d'Hibiscus NANDA SÁ, format 50 ml, Guinée-Bissau", en: "NANDA SÁ Hibiscus Oil, 50 ml, Guinea-Bissau", pt: "Óleo de Hibisco NANDA SÁ, 50 ml, Guiné-Bissau" }, locale)}
 className="w-full h-full object-contain p-8"
 loading="eager"
 />
 <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
 <div>
 <div className="text-xs tracking-[0.2em] uppercase text-nanda-muted">Signature</div>
 <div className="font-serif text-2xl text-nanda-ink mt-1">Hibiscus Oil</div>
 </div>
 <div className="text-xs tracking-wider text-nanda-muted">50 ml</div>
 </div>
 </div>
 </FadeUp>
 </div>
 </div>
 </section>

 {/* ================= VALUES ================= */}
 <Section className="bg-white">
 <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
 <div className="lg:col-span-4">
 <Overline>Valeurs</Overline>
 <h2 className="font-serif text-4xl md:text-5xl leading-tight text-nanda-ink">{t.valuesTitle}</h2>
 </div>
 <div className="lg:col-span-8">
 <Stagger className="grid grid-cols-2 md:grid-cols-5 gap-6 md:gap-8">
 {VALUES.map((v) => (
 <StaggerItem key={v.key}>
 <div className="flex flex-col items-start gap-3 p-6 border border-nanda-line hover:border-[#4A5D4E] transition-colors" data-testid={`value-${v.key}`}>
 <v.icon className="w-6 h-6 text-[#4A5D4E]" strokeWidth={1.5} />
 <span className="font-serif text-lg text-nanda-ink">{pick({ fr: v.fr, en: v.en, pt: v.pt }, locale)}</span>
 </div>
 </StaggerItem>
 ))}
 </Stagger>
 </div>
 </div>
 </Section>

 {/* ================= CATEGORIES ================= */}
 <Section className="bg-[#FAF7F1]">
 <Overline>{t.categoriesTitle}</Overline>
 <h2 className="font-serif text-4xl md:text-5xl leading-tight text-nanda-ink max-w-3xl">
 {{ fr: "Sept univers, un seul geste.", en: "Seven universes, one gesture.", pt: "Sete universos, um só gesto." }[locale]}
 </h2>
 <p className="mt-6 text-lg text-nanda-muted max-w-2xl">{t.categoriesSubtitle}</p>

 <Stagger className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
 {CATEGORIES.map((c) => (
 <StaggerItem key={c.key}>
 <div
 className="group bg-white border border-nanda-line p-8 h-full hover:shadow-[0_20px_50px_-30px_rgba(0,0,0,0.2)] transition-all"
 data-testid={`category-${c.key}`}
 >
 <div className="label-overline text-[#4A5D4E] mb-4">{c.key === "botanical" || c.key === "shea" || c.key === "aloe" ? "Collection" : "Category"}</div>
 <h3 className="font-serif text-2xl text-nanda-ink leading-tight mb-4">{pick(c.label, locale)}</h3>
 <p className="text-sm text-nanda-muted leading-relaxed">{pick(c.desc, locale)}</p>
 <div className="mt-6 flex items-center gap-2 text-xs tracking-widest uppercase text-nanda-ink opacity-0 group-hover:opacity-100 transition-opacity">
 {{ fr: "Bientôt disponible", en: "Coming soon", pt: "Brevemente" }[locale]}
 <ArrowUpRight className="w-3 h-3" />
 </div>
 </div>
 </StaggerItem>
 ))}
 </Stagger>
 </Section>

 {/* ================= PRODUCTS SIGNATURE ================= */}
 <Section id="collection" className="bg-white">
 <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
 <div>
 <Overline>{{ fr: "Collection Signature", en: "Signature Collection", pt: "Colecção Signature" }[locale]}</Overline>
 <h2 className="font-serif text-4xl md:text-5xl leading-tight text-nanda-ink">{t.productsTitle}</h2>
 </div>
 <p className="text-lg text-nanda-muted max-w-md">{t.productsSubtitle}</p>
 </div>

 <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
 {HERO_PRODUCTS.map((p) => (
 <StaggerItem key={p.slug}>
 <div className="group flex flex-col" data-testid={`product-${p.slug}`}>
 <div className="relative aspect-[3/4] bg-[#F7F3EC] overflow-hidden">
 <img
 src={p.image}
 alt={`${pick(p.name, locale)} NANDA SÁ ${p.volume} — ${p.origin}`}
 className="w-full h-full object-contain p-6 group-hover:scale-105 transition-transform duration-700"
 loading="lazy"
 />
 </div>
 <div className="mt-6 flex flex-col gap-2">
 <div className="text-xs tracking-[0.2em] uppercase text-nanda-muted">{pick(p.category, locale)}</div>
 <h3 className="font-serif text-xl text-nanda-ink">{pick(p.name, locale)}</h3>
 <div className="text-xs text-nanda-muted italic">{pick(p.benefits, locale)}</div>
 <div className="mt-2 flex items-center justify-between text-xs tracking-wider text-nanda-muted uppercase">
 <span>{p.volume}</span>
 <span>{p.origin}</span>
 </div>
 </div>
 </div>
 </StaggerItem>
 ))}
 </Stagger>
 </Section>

 {/* ================= INGREDIENTS ================= */}
 <Section className="bg-[#1E2A22] text-white">
 <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
 <div className="lg:col-span-4">
 <Overline>
 <span className="text-white/60">{{ fr: "Ingrédients", en: "Ingredients", pt: "Ingredientes" }[locale]}</span>
 </Overline>
 <h2 className="font-serif text-4xl md:text-5xl leading-tight">{t.ingredientsTitle}</h2>
 <p className="mt-6 text-white/70 leading-relaxed">{t.ingredientsSubtitle}</p>
 </div>
 <div className="lg:col-span-8">
 <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
 {INGREDIENTS.map((ing) => {
 const slugMap = { "Karité": "karite", "Baobab": "baobab", "Moringa": "moringa", "Hibiscus": "hibiscus", "Aloe Vera": "aloe" };
 const slug = slugMap[ing.name];
 const content = (
 <>
 <div className="flex items-baseline justify-between gap-4">
 <h3 className="font-serif text-2xl">
 {ing.name}
 {slug && (
 <ArrowUpRight className="inline w-4 h-4 ml-2 text-[#B8935E] opacity-0 group-hover:opacity-100 transition-opacity" />
 )}
 </h3>
 <span className="text-xs italic text-white/50">{ing.latin}</span>
 </div>
 <p className="mt-3 text-sm text-white/70 leading-relaxed">{pick({ fr: ing.fr, en: ing.en, pt: ing.pt }, locale)}</p>
 {slug && (
 <span className="mt-3 inline-block text-[10px] tracking-[0.3em] uppercase text-[#B8935E]">
 {{ fr: "Lire la fiche", en: "Read the sheet", pt: "Ler a ficha" }[locale]} →
 </span>
 )}
 </>
 );
 if (slug) {
 return (
 <Link
 key={ing.name}
 to={`/cosmetiques/ingredients/${slug}`}
 className="group block border-t border-white/20 pt-6 hover:bg-white/[0.03] transition-colors -mx-3 px-3 pb-3"
 data-testid={`ingredient-${ing.name.toLowerCase}`}
 >
 {content}
 </Link>
 );
 }
 return (
 <div key={ing.name} className="border-t border-white/20 pt-6" data-testid={`ingredient-${ing.name.toLowerCase}`}>
 {content}
 </div>
 );
 })}
 </div>
 </div>
 </div>
 </Section>

 {/* ================= RITUAL / PROCESS ================= */}
 <Section className="bg-white">
 <div className="text-center max-w-3xl mx-auto">
 <Overline>{{ fr: "Savoir-faire", en: "Know-how", pt: "Saber-fazer" }[locale]}</Overline>
 <h2 className="font-serif text-4xl md:text-5xl leading-tight text-nanda-ink">{t.ritualTitle}</h2>
 <p className="mt-6 text-lg text-nanda-muted">{t.ritualSubtitle}</p>
 </div>

 <Stagger className="mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
 {RITUAL.map((r) => (
 <StaggerItem key={r.step}>
 <div className="flex flex-col gap-4" data-testid={`ritual-step-${r.step}`}>
 <div className="text-6xl font-serif text-[#B8935E] leading-none">{r.step}</div>
 <h3 className="font-serif text-xl text-nanda-ink">{pick({ fr: r.fr, en: r.en, pt: r.pt }, locale)}</h3>
 <p className="text-sm text-nanda-muted leading-relaxed">{pick({ fr: r.descFr, en: r.descEn, pt: r.descPt }, locale)}</p>
 </div>
 </StaggerItem>
 ))}
 </Stagger>
 </Section>

 {/* ================= FAQ ================= */}
 <Section className="bg-[#FAF7F1]">
 <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
 <div className="lg:col-span-4">
 <Overline>FAQ</Overline>
 <h2 className="font-serif text-4xl md:text-5xl leading-tight text-nanda-ink">{t.faqTitle}</h2>
 </div>
 <div className="lg:col-span-8">
 <div className="divide-y divide-nanda-line border-t border-b border-nanda-line">
 {FAQ.map((f, i) => (
 <div key={i} data-testid={`faq-item-${i}`}>
 <button
 onClick={() => setOpenFaq(openFaq === i ? null : i)}
 className="w-full text-left py-6 flex items-center justify-between gap-6 hover:text-[#4A5D4E] transition-colors"
 data-testid={`faq-toggle-${i}`}
 >
 <span className="font-serif text-lg text-nanda-ink">{pick(f.q, locale)}</span>
 <ChevronDown className={`w-5 h-5 flex-shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
 </button>
 {openFaq === i && (
 <p className="pb-6 text-nanda-muted leading-relaxed" data-testid={`faq-answer-${i}`}>
 {pick(f.a, locale)}
 </p>
 )}
 </div>
 ))}
 </div>
 </div>
 </div>
 </Section>

 {/* ================= DOWNLOADS ================= */}
 <Section className="bg-white" id="telechargements">
 <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
 <div>
 <Overline>
 {{ fr: "Téléchargements", en: "Downloads", pt: "Descargas" }[locale]}
 </Overline>
 <h2 className="font-serif text-4xl md:text-5xl leading-tight text-nanda-ink">
 {{
 fr: "Nos livrables",
 en: "Our brochures",
 pt: "Os nossos livretes",
 }[locale]}
 </h2>
 </div>
 <p className="text-nanda-muted max-w-md">
 {{
 fr: "Deux documents PDF trilingues (EN·PT·FR), prêts à consulter ou à imprimer.",
 en: "Two trilingual PDF documents (EN·PT·FR), ready to view or print.",
 pt: "Dois documentos PDF trilingues (EN·PT·FR), prontos a consultar ou imprimir.",
 }[locale]}
 </p>
 </div>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
 <a
 href="/pdf/nanda-cosmetics-catalogue-2026.pdf"
 target="_blank"
 rel="noreferrer"
 className="group block border border-nanda-line p-8 hover:border-[#B8935E] transition-colors"
 data-testid="download-catalogue-brand-book"
 >
 <div className="flex items-start justify-between gap-4">
 <div>
 <div className="text-xs tracking-[0.3em] uppercase text-[#B8935E] mb-3">
 {{ fr: "Brand Book · Volume I", en: "Brand Book · Volume I", pt: "Brand Book · Volume I" }[locale]}
 </div>
 <h3 className="font-serif text-2xl md:text-3xl text-nanda-ink leading-tight">
 {{
 fr: "Catalogue Cosmétique 2026",
 en: "2026 Cosmetics Catalogue",
 pt: "Catálogo Cosmético 2026",
 }[locale]}
 </h3>
 <p className="mt-3 text-sm text-nanda-muted italic font-serif">
 {{
 fr: "26 pages, éditorial, style Aesop / Le Labo. Manifeste, six produits, bibliothèque botanique.",
 en: "26 pages, editorial, Aesop / Le Labo style. Manifesto, six products, botanical library.",
 pt: "26 páginas, editorial, estilo Aesop / Le Labo. Manifesto, seis produtos, biblioteca botânica.",
 }[locale]}
 </p>
 </div>
 <ArrowUpRight className="w-5 h-5 text-[#B8935E] flex-shrink-0 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
 </div>
 <div className="mt-8 flex items-center justify-between text-xs uppercase tracking-wider text-nanda-muted border-t border-nanda-line pt-4">
 <span>PDF · 12 MB · 26 {{ fr: "pages", en: "pages", pt: "páginas" }[locale]}</span>
 <span className="text-[#B8935E]">EN · PT · FR</span>
 </div>
 </a>

 <a
 href="/pdf/nanda-cosmetics-b2b-distribution-2026.pdf"
 target="_blank"
 rel="noreferrer"
 className="group block border border-nanda-line p-8 hover:border-[#B8935E] transition-colors bg-[#FAF7F1]"
 data-testid="download-catalogue-b2b"
 >
 <div className="flex items-start justify-between gap-4">
 <div>
 <div className="text-xs tracking-[0.3em] uppercase text-[#A83E2C] mb-3">
 {{ fr: "Confidentiel · B2B", en: "Confidential · B2B", pt: "Confidencial · B2B" }[locale]}
 </div>
 <h3 className="font-serif text-2xl md:text-3xl text-nanda-ink leading-tight">
 {{
 fr: "Livret Distribution 2026",
 en: "2026 Distribution Booklet",
 pt: "Livrete Distribuição 2026",
 }[locale]}
 </h3>
 <p className="mt-3 text-sm text-nanda-muted italic font-serif">
 {{
 fr: "12 pages pour hôtels, spas, retailers et distributeurs. Programmes, conditions commerciales, onboarding.",
 en: "12 pages for hotels, spas, retailers and distributors. Programmes, commercial conditions, onboarding.",
 pt: "12 páginas para hotéis, spas, retalho e distribuidores. Programas, condições comerciais, onboarding.",
 }[locale]}
 </p>
 </div>
 <ArrowUpRight className="w-5 h-5 text-[#B8935E] flex-shrink-0 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
 </div>
 <div className="mt-8 flex items-center justify-between text-xs uppercase tracking-wider text-nanda-muted border-t border-nanda-line pt-4">
 <span>PDF · 5.3 MB · 12 {{ fr: "pages", en: "pages", pt: "páginas" }[locale]}</span>
 <span className="text-[#B8935E]">EN · PT · FR</span>
 </div>
 </a>
 </div>

 <a
 href="/pdf/nanda-cosmetics-catalogue-v6.zip"
 className="mt-6 inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-nanda-muted hover:text-[#B8935E] transition-colors"
 data-testid="download-zip-full"
 >
 ↓ {{
 fr: "Télécharger le pack complet (ZIP · 41 MB · PDFs + sources HTML/CSS/Python + previews JPG)",
 en: "Download the full pack (ZIP · 41 MB · PDFs + HTML/CSS/Python sources + JPG previews)",
 pt: "Descarregar o pacote completo (ZIP · 41 MB · PDFs + código-fonte HTML/CSS/Python + previews JPG)",
 }[locale]}
 </a>
 </Section>

 {/* ================= FINAL CTA ================= */}
 <Section className="bg-[#1E2A22] text-white text-center">
 <div className="max-w-3xl mx-auto">
 <h2 className="font-serif text-4xl md:text-5xl leading-tight">{t.ctaFinal.title}</h2>
 <p className="mt-6 text-lg text-white/70">{t.ctaFinal.sub}</p>
 <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
 <Link
 to="/contact"
 className="inline-flex items-center gap-2 bg-white text-nanda-ink px-8 py-4 text-sm tracking-wider uppercase hover:bg-[#B8935E] hover:text-white transition-colors"
 data-testid="cosmetiques-cta-final"
 >
 {t.ctaFinal.button}
 <ArrowUpRight className="w-4 h-4" />
 </Link>
 <Link
 to="/cosmetiques/amenity-kit"
 className="inline-flex items-center gap-2 border border-white/40 text-white px-6 py-4 text-sm tracking-wider uppercase hover:border-[#B8935E] hover:text-[#B8935E] transition-colors"
 data-testid="cosmetiques-cta-amenity"
 >
 {{ fr: "Configurer un Kit Amenity", en: "Configure an Amenity Kit", pt: "Configurar um Kit Amenity" }[locale]}
 <ArrowUpRight className="w-4 h-4" />
 </Link>
 <Link
 to="/carnet"
 className="inline-flex items-center gap-2 border border-white/40 text-white px-6 py-4 text-sm tracking-wider uppercase hover:border-[#B8935E] hover:text-[#B8935E] transition-colors"
 data-testid="cosmetiques-cta-carnet"
 >
 {{ fr: "Lire le Carnet des ingrédients", en: "Read the ingredient Journal", pt: "Ler o Caderno de ingredientes" }[locale]}
 <ArrowUpRight className="w-4 h-4" />
 </Link>
 </div>
 </div>
 </Section>
 </div>
 );
}
